import { BOOKING_URL } from "../mock";

export const LOGISTICS = {
  seo: {
    title: "Logistics Orchestration — Order aggregation, auto-dispatch, route planning & proof of delivery",
    description:
      "Fleetroot logistics orchestration: aggregate orders from every channel, pool and auto-dispatch on demand or batch and route on schedule, execute in the driver app and close with digital proof of delivery and automated billing.",
  },
  hero: {
    eyebrow: "Platform · Logistics orchestration",
    title: "Every order, every channel. On demand or on schedule.",
    subtitle:
      "One flow from order to proof: aggregate orders from every channel, pool and dispatch in real time, or plan tomorrow's routes tonight — all on one connected platform.",
    primary: { label: "Request a demo", to: "/contact" },
    secondary: { label: "Book a 30-min call", href: BOOKING_URL, external: true },
    trust: ["On-demand & scheduled logistics workflow", "Own fleet, 3PL or both", "Multi-driver, multi-vehicle operations", "ERP & e-commerce connectors"],
  },
  problem: {
    eyebrow: "The problem",
    title: "Deliveries don't fail in the van. They fail in the hand-offs.",
    text: "Between the order and the doorstep, there are five hand-offs. When each one lives in a different tool — a portal, a spreadsheet, a chat group, a phone — every hand-off can leak time, cost and visibility.",
    pains: [
      { icon: "Inbox", title: "Intake → dispatch: orders wait in queues", text: "Orders sit across channels and queues before operations can act on them." },
      { icon: "Route", title: "Dispatch → routing: routes built by hand", text: "Capacity and time windows are managed manually, making efficient planning harder." },
      { icon: "MessageSquareWarning", title: "Routing → driver: instructions over chat", text: "Wrong addresses, missed stops, and no record of what changed on the road." },
      { icon: "BellOff", title: "Driver → customer: limited visibility until delivery", text: "Customers have little visibility into delivery progress, delays or failed attempts." },
      { icon: "Receipt", title: "Delivery → invoice: month-end reconciliation", text: "Delivered orders still need to be reconciled before billing is complete." },
    ],
  },
  modes: {
    eyebrow: "Two modes. One platform",
    title: "On-demand and scheduled logistics, all on one platform.",
    subtitle: "Fleetroot supports both on-demand and scheduled logistics operations — from rapid, time-sensitive deliveries to planned routes and replenishment. Choose the operating model that fits your business, all within the same platform.",
    lanes: [
      {
        id: "on-demand",
        label: "On-demand",
        tagline: "Minutes from order to door",
        examples: "Pharmacy, food & grocery, q-commerce, spare parts",
        points: ["Orders pooled the second they arrive", "Nearest branch with stock, best-placed rider", "SLA timer on every order, alerts before it slips", "Live tracking link sent automatically"],
      },
      {
        id: "scheduled",
        label: "Scheduled",
        tagline: "Planned routes, fuller loads",
        examples: "Distribution & 3PL, B2B replenishment, next-day e-commerce, returns",
        points: ["Orders batched by cut-off, zone and depot", "Capacity- and time-window-aware routes in minutes", "Loading sequence and manifests per vehicle", "Customer picks a slot, gets an ETA the night before"],
      },
    ],
    shared: ["One order pool", "One driver app", "One proof of delivery", "One billing run"],
  },
  stages: {
    eyebrow: "How orchestration works",
    title: "From order to invoice in six moves.",
    subtitle: "Pick a stage. Each one hands off to the next automatically — on demand or on schedule.",
    items: [
      {
        id: "aggregate",
        label: "Order aggregation",
        icon: "Inbox",
        title: "Every channel into one order pool",
        text: "Orders from your app, website, call centre, marketplaces, ERP or CSV land in one pool with address, time window, items and payment type already structured.",
        bullets: ["Shopify, WooCommerce, Zoho & marketplaces", "ERP connectors and REST API / webhooks", "Address cleansing and geocoding", "Duplicate and split-order handling"],
        visual: "orders",
      },
      {
        id: "dispatch",
        label: "Pooling & dispatch",
        icon: "Shuffle",
        title: "The right rider, automatically",
        text: "On-demand orders are pooled and assigned the moment they arrive; scheduled orders are batched at cut-off. Rules decide by distance, stock, capacity, skills and SLA.",
        bullets: ["Nearest branch with stock + best-placed rider", "Order pooling across channels and merchants", "Hand-off to 3PL carriers when your fleet is full", "Manual override from the dispatch board"],
        visual: "dispatch",
      },
      {
        id: "routing",
        label: "Route planning",
        icon: "Route",
        title: "Optimised routes in minutes, not mornings",
        text: "Multi-stop routes that respect vehicle capacity, time windows, driver shifts and live traffic — re-planned when the day changes.",
        bullets: ["Up to 500 stops per plan", "Capacity, time windows and shift limits", "Dynamic re-routing for new or failed stops", "Zone and territory planning"],
        visual: "route",
      },
      {
        id: "execute",
        label: "Driver execution",
        icon: "Smartphone",
        title: "Everything drivers need, in one app",
        text: "Stop sequence, navigation, status updates and one-tap proof — signature, photo or OTP — on iOS and Android, built for low-bandwidth networks.",
        bullets: ["Turn-by-turn navigation per stop", "Signature, photo & OTP proof", "Cash on delivery and returns", "Failed-delivery reason codes"],
        visual: "driver",
      },
      {
        id: "customer",
        label: "Customer updates",
        icon: "MessageSquare",
        title: "Customers know before they ask",
        text: "Branded tracking links, live ETAs and automatic WhatsApp, SMS or email at every milestone — with self-serve rescheduling for scheduled deliveries.",
        bullets: ["Live tracking link per order", "ETA and “driver nearby” notifications", "Slot selection and rescheduling", "Ratings and feedback at the door"],
        visual: "notify",
      },
      {
        id: "billing",
        label: "Billing & analytics",
        icon: "Receipt",
        title: "The day closes with billing and insight.",
        text: "Invoices per merchant generated from delivered tasks, SLA reports per client and cost per drop by route, driver and zone.",
        bullets: ["Merchant billing from delivered tasks", "SLA and on-time reports per client", "Cost per drop, per route, per driver", "Exports and BI connectors"],
        visual: "billing",
      },
    ],
  },
  fleet: {
    eyebrow: "Own fleet, 3PL or both",
    title: "Orchestrate your own fleet, 3PLs, or both.",
    items: [
      { icon: "Truck", title: "Your own fleet", text: "Riders and vehicles managed through the driver app, with telematics feeding ETAs and fleet visibility." },
      { icon: "Handshake", title: "Third-party carriers", text: "Assign tasks to 3PLs and bring their tracking back into one dashboard and one proof of delivery record." },
      { icon: "GitMerge", title: "Hybrid, by rule", text: "Keep core volume in-house and overflow to carriers automatically when capacity or SLA demands it." },
    ],
  },
  reports: {
    eyebrow: "Analytics · the KPIs orchestration moves",
    title: "Every order becomes a data point. Every data point, a decision.",
    subtitle: "The Data Analytics module turns orders, SLAs, cancellations and rider performance into one live view — and these are the numbers it typically moves once dispatch, routing and proof run in one flow.",
    caption: "Actual product screens · Data Analytics module",
    shots: [
      { id: "overview", label: "Overview", src: "/platform/analytics-overview.webp", alt: "Data Analytics overview — orders, value and outcomes, service level KPIs",
        headline: ["See what needs action.", "Now."], stats: [["85.0%", "completion rate"], ["617 / 568", "open orders / past SLA"], ["7h 24m", "average order-to-delivery"]] },
      { id: "orders", label: "Orders & revenue", src: "/platform/analytics-orders.webp", alt: "Daily orders and order value chart with busiest and quietest day",
        headline: ["Your average day does not deliver.", "Your peak hour does."], stats: [["1,376 vs 809", "orders on the busiest day vs the quietest"], ["AED 379K", "highest-value day"], ["0", "days with no orders"]] },
      { id: "busiest", label: "Busiest times", src: "/platform/analytics-busiest.webp", alt: "Busiest times heatmap — orders by weekday and hour",
        headline: ["Staff the heatmap,", "not the average."], stats: [["11:00–15:00", "order creation peaks"], ["Thursday", "busiest weekday"], ["00:00–06:00", "near-zero demand — rebalance shifts"]] },
      { id: "sla", label: "SLA & delivery", src: "/platform/analytics-sla.webp", alt: "SLA by stage — where the promise breaks",
        headline: ["79.7% SLA compliance", "hides where the promise breaks."], stats: [["80.5%", "of breaches happen at order acceptance"], ["6h 18m", "average delay when late"], ["07:00–08:00", "73.5% breach rate"]] },
      { id: "exceptions", label: "Exceptions", src: "/platform/analytics-exceptions.webp", alt: "Exceptions analysis — exception count by type",
        headline: ["A cancelled order is not a KPI.", "It is a pharmacy without stock."], stats: [["212", "pharmacy closed"], ["49", "address not found"], ["33", "customer not available"]] },
    ],
    more: ["Live operations", "Locations", "Products & customers", "Payments", "Insights"],
    kpis: [
      { value: "-30%", label: "reattempts — accurate ETAs and receiver notifications" },
      { value: "-25%", label: "cost per drop — fuller loads, shorter optimised routes" },
      { value: "95%+", label: "on-time rate against promised windows" },
      { value: "-40%", label: "“where is my order?” calls — live tracking and proactive updates" },
      { value: "Hours", label: "for month-end merchant billing, not days" },
      { value: "100%", label: "orders closed with digital proof of delivery" },
    ],
  },
  industries: {
    eyebrow: "Built for both modes",
    title: "Built for the way your operation works.",
    items: [
      { icon: "Pill", title: "Pharmacy & healthcare", text: "45-minute prescriptions on demand; clinic and branch replenishment on schedule." },
      { icon: "UtensilsCrossed", title: "Food & grocery", text: "Hot food and q-commerce in minutes; weekly supplier runs planned by route." },
      { icon: "ShoppingBag", title: "E-commerce & retail", text: "Same-day from store, next-day from the warehouse, returns picked up on the way." },
      { icon: "Warehouse", title: "Distribution & 3PL", text: "Multi-depot, multi-client scheduled routes with per-merchant billing." },
      { icon: "Store", title: "Marketplaces", text: "Multi-vendor pickups managed in one operation, with SLA performance tracked per vendor." },
      { icon: "Wrench", title: "Spare parts & field supply", text: "Urgent parts dispatched on demand; technician van restocking planned on schedule." },
    ],
  },
  faq: [
    { q: "Can we run on-demand and scheduled deliveries in the same account?", a: "Fleetroot supports both on-demand and scheduled delivery operations, with each following its own planning workflow. On-demand orders can be dispatched as they arrive, while scheduled orders can be planned and routed in advance for a future delivery date. On-demand orders can also be assigned a future delivery date when required." },
    { q: "How do orders get into Fleetroot?", a: "Native connectors for Shopify, WooCommerce, Zoho and major ERPs (NetSuite, Dynamics 365, SAP, Odoo), a REST API with webhooks, CSV import, or manual entry from the dispatch board and call centre." },
    { q: "Can we use third-party carriers alongside our own fleet?", a: "Yes. Hand tasks to 3PLs by rule or manually, and their tracking and proof come back into the same dashboard so your team and customers see one view." },
    { q: "How does billing work for multiple merchants?", a: "Each delivered task carries the merchant, service type and rate card. At month-end (or any period) Fleetroot generates statements per merchant automatically, with SLA reports alongside." },
    { q: "What counts as a task?", a: "One task equals one pickup, delivery, or service stop. A pickup and delivery count as two tasks, while reattempts on the same stop do not create an additional task." },
    { q: "How long does it take to go live?", a: "Most operations run live deliveries within days. ERP integrations and multi-warehouse setups typically take two to three weeks with a dedicated onboarding manager." },
  ],
  cta: {
    title: "See your orders orchestrated, end to end.",
    text: "Tell us what you deliver, how many orders a day and whether they're on demand, scheduled or both. We'll set up a live demo on your real routes.",
    cta: { label: "Request a demo", to: "/contact" },
    secondary: { label: "Book a 30-min call", href: BOOKING_URL, external: true },
  },
};
